// build: preview-cache-guard 2026-06-03
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { installPreviewRuntimeRecovery, runPreviewCacheGuard } from './utils/previewCacheGuard'
import { registerAppServiceWorker } from './pwa/registerServiceWorker'
import ErrorBoundary from './components/common/ErrorBoundary'
import { startViewportOffsetTracker } from './lib/viewportOffsetTracker'

// Proteção secundária contra boot duplicado por carregadores externos ou extensões.
// O build usa somente os hashes nativos do Vite, mantendo uma URL única para cada módulo.
// A marca fica em `window` porque é compartilhada entre eventuais instâncias do módulo.
declare global {
  interface Window {
    __AUTOFLUXO_APP_BOOTED__?: boolean;
  }
}

const bootstrap = () => {
  if (typeof window === 'undefined') return;

  // Recupera abas de preview que ficaram abertas durante uma reotimização do Vite.
  installPreviewRuntimeRecovery();

  if (window.__AUTOFLUXO_APP_BOOTED__) {
    console.warn('[main] Boot duplicado ignorado (bundle carregado mais de uma vez).');
    return;
  }
  window.__AUTOFLUXO_APP_BOOTED__ = true;

  // Limpeza idempotente: desregistra SW e limpa Cache Storage apenas no preview/dev.
  // Em produção, é no-op. Preserva localStorage de autenticação.
  void runPreviewCacheGuard();

  // Registro do Service Worker (somente produção real; bloqueado em dev/preview/iframe).
  void registerAppServiceWorker();

  // Detecta dispositivo móvel real (touch + sem hover fino)
  const isRealMobileDevice =
    window.matchMedia('(pointer: coarse)').matches &&
    window.matchMedia('(hover: none)').matches &&
    (navigator.maxTouchPoints ?? 0) > 0;

  // Bloquear zoom APENAS em mobile. No desktop, permitir zoom normalmente.
  if (isRealMobileDevice) {
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault();
    });

    document.addEventListener('gesturechange', function (e) {
      e.preventDefault();
    });

    document.addEventListener('gestureend', function (e) {
      e.preventDefault();
    });

    // Prevenir zoom com Ctrl/Cmd + scroll (mobile com teclado externo)
    document.addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    }, { passive: false });

    // Prevenir zoom com teclado (mobile com teclado externo)
    document.addEventListener('keydown', function (e) {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '=')) {
        e.preventDefault();
      }
    });
  }

  // Bloquear orientação para portrait apenas em dispositivos móveis reais
  if (isRealMobileDevice && screen.orientation && (screen.orientation as any).lock) {
    (screen.orientation as any).lock('portrait').catch(() => {
      // Silently fail - não suportado em todos os browsers
    });
  }

  const container = document.getElementById('root')!;

  // Segurança extra: se sobrou conteúdo órfão dentro do #root, remover antes de montar.
  if (container.childNodes.length > 0) {
    container.replaceChildren();
  }

  // Antes do primeiro render, erros inesperados mostram o fallback com "Recarregar aplicativo".
  const w = window as any;
  const onBootError = () => {
    if (!w.__AUTOFLUXO_RENDERED__ && container.childNodes.length === 0) {
      w.__autofluxoShowBootFallback?.('Não foi possível carregar.');
    }
  };
  window.addEventListener('error', onBootError);
  window.addEventListener('unhandledrejection', onBootError);

  // Sem boundary na raiz, um erro no próprio App deixa a tela em branco sem saída.
  // iOS standalone: garante que o app abra com o viewport no topo (sem deslocamento herdado do login).
  try {
    const nav = window.navigator as Navigator & { standalone?: boolean };
    if (nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches) {
      window.scrollTo(0, 0);
      window.addEventListener('load', () => window.scrollTo(0, 0), { once: true });
    }
  } catch { /* ignora */ }
  try { startViewportOffsetTracker(); } catch { /* diagnóstico opcional */ }
  createRoot(container).render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>,
  );

  window.setTimeout(() => {
    if (container.childNodes.length > 0) {
      w.__AUTOFLUXO_RENDERED__ = true;
      window.removeEventListener('error', onBootError);
      window.removeEventListener('unhandledrejection', onBootError);
    }
  }, 1500);
};

bootstrap();
